import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';

const useStyles = createUseStyles({
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeIn: {
    opacity: 0,
    animation: '$fadeIn 0.5s ease-in-out forwards',
  },
  hidden: {
    opacity: 0,
  },
  card: {
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    padding: '15px',
    backgroundColor: 'white',
    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',
    },
  },
  title: {
    margin: '0',
    fontSize: '16px',
    fontWeight: 600,
    color: '#282828',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  subtitle: {
    fontSize: '12px',
    fontWeight: 400,
    fill: '#666666',
    transition: 'fill 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      fill: '#b3b3b3',
    },
  },
  total: {
    fontSize: '24px',
    fontWeight: 600,
    fill: '#282828',
    transition: 'fill 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      fill: '#ffffff',
    },
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    alignContent: 'center',
    minHeight: '85px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    margin: '5px',
  },
  legendColor: {
    marginRight: '8px',
    borderRadius: '3px',
    width: '12px',
    height: '12px',
  },
  legendLabel: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 400,
    color: '#666666',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  tooltip: {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.4) inset',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '100px',
    padding: '8px 16px',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    textAlign: 'center',
    transition: 'all 0.15s ease-out',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: 'rgba(26, 26, 26, 0.8)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
    },
  },
  tooltipLabel: {
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      '& .tooltip-value': {
        color: '#b3b3b3 !important',
      },
    },
  },
});

interface PieChartEntry {
  value: number;
  label: string;
  color: string;
}

interface PieChartWidgetProps {
  title: string;
  subtitle: string;
  data: PieChartEntry[];
  isLoading: boolean;
}

const CustomTooltip = ({ active, payload }: TooltipProps) => {
  const classes = useStyles();

  if (active && payload && payload.length) {
    const data = payload[0].payload as PieChartEntry;

    return (
      <div className={classes.tooltip}>
        <p
          className={`ion-no-margin ${classes.tooltipLabel}`}
          style={{
            fontSize: '15px',
          }}
        >
          <span style={{ color: data.color, fontWeight: 600 }}>{data.label}</span>
          <span style={{ color: '#666666', fontWeight: 400 }}> {data.value}</span>
        </p>
      </div>
    );
  }
  return null;
};

const PieChartWidget: React.FC<PieChartWidgetProps> = ({ title, subtitle, data, isLoading }) => {
  const classes = useStyles();

  const contentClass = isLoading ? classes.hidden : classes.fadeIn;
  const total = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0;
    return data.reduce((sum, entry) => {
      const value = Number(entry?.value) || 0;
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  }, [data]);

  return (
    <div className={classes.card}>
      <div className={contentClass}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <h2 className={classes.title}>{title}</h2>
        </div>
        <div style={{ margin: '15px 0', height: '180px' }}>
          {data && data.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={85 * 0.85}
                  outerRadius={85}
                  paddingAngle={1}
                  stroke="none"
                  strokeWidth={0}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry?.color || '#D9D9D9'}
                      stroke="none"
                      strokeWidth={0}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} allowEscapeViewBox={{ x: true, y: true }} />
                <text
                  x="50%"
                  y="55%"
                  textAnchor="middle"
                  dominantBaseline="text-after-edge"
                  className={classes.total}
                >
                  {total}
                </text>
                <text
                  x="50%"
                  y="55%"
                  textAnchor="middle"
                  dominantBaseline="text-before-edge"
                  className={classes.subtitle}
                >
                  {subtitle}
                </text>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <p className={classes.subtitle}>No data available</p>
            </div>
          )}
        </div>

        <div className={classes.legend}>
          {data &&
            data.map((entry, index) => (
              <div key={index} className={classes.legendItem}>
                <span
                  className={classes.legendColor}
                  style={{ backgroundColor: entry?.color || '#D9D9D9' }}
                />
                <p className={classes.legendLabel}>{entry?.label || 'Unknown'}</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default PieChartWidget;
